import React, { useRef, useState, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, ContactShadows, Line, Html, useTexture } from "@react-three/drei";
import * as THREE from "three";

export const REAL_SENSOR_IMG =
  "https://upload.wikimedia.org/wikipedia/commons/8/87/TPMS_front_side.jpg";

export const isWebGLSupported = () => {
  try {
    const c = document.createElement("canvas");
    return !!(
      window.WebGLRenderingContext &&
      (c.getContext("webgl") || c.getContext("experimental-webgl"))
    );
  } catch {
    return false;
  }
};

export class CanvasBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch() {}
  render() {
    if (this.state.failed) return this.props.fallback || null;
    return this.props.children;
  }
}

/* ---------- Tire + rim ---------- */
const Spokes = () => {
  const spokes = [];
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    spokes.push(
      <mesh key={i} rotation={[0, 0, a]} position={[0, 0, 0]}>
        <boxGeometry args={[0.1, 1.08, 0.14]} />
        <meshStandardMaterial color="#c7ccd6" metalness={0.95} roughness={0.22} />
      </mesh>
    );
  }
  return <group position={[0, 0, 0.24]}>{spokes}</group>;
};

const Tire = () => (
  <group>
    <mesh>
      <torusGeometry args={[1.05, 0.42, 36, 90]} />
      <meshStandardMaterial color="#16171c" roughness={0.92} metalness={0.08} />
    </mesh>
    <mesh position={[0, 0, 0]}>
      <torusGeometry args={[1.05, 0.425, 12, 60]} />
      <meshStandardMaterial color="#1d1f26" roughness={0.85} wireframe={false} />
    </mesh>
    <mesh rotation={[Math.PI / 2, 0, 0]}>
      <cylinderGeometry args={[0.66, 0.66, 0.5, 48, 1, true]} />
      <meshStandardMaterial color="#2a2e38" metalness={0.9} roughness={0.3} side={2} />
    </mesh>
    <Spokes />
    <mesh position={[0, 0, 0.26]} rotation={[Math.PI / 2, 0, 0]}>
      <cylinderGeometry args={[0.2, 0.2, 0.16, 32]} />
      <meshStandardMaterial color="#e2e8f0" metalness={1} roughness={0.15} />
    </mesh>
    <mesh position={[0, 0, 0.26]} rotation={[Math.PI / 2, 0, 0]}>
      <cylinderGeometry args={[0.07, 0.07, 0.2, 16]} />
      <meshStandardMaterial color="#E63946" emissive="#E63946" emissiveIntensity={0.6} />
    </mesh>
    {/* brake caliper hint */}
    <mesh position={[0.5, 0.35, 0.05]}>
      <boxGeometry args={[0.22, 0.42, 0.18]} />
      <meshStandardMaterial color="#b91c1c" metalness={0.4} roughness={0.5} />
    </mesh>
  </group>
);

/* ---------- Real TPMS sensor photo inside the 3D space ---------- */
const RealSensorCard = () => {
  const tex = useTexture(REAL_SENSOR_IMG);
  tex.colorSpace = THREE.SRGBColorSpace;
  return (
    <group rotation={[0, -0.28, 0.04]}>
      {/* metallic frame */}
      <mesh position={[0, 0, -0.035]}>
        <planeGeometry args={[1.86, 1.24]} />
        <meshStandardMaterial color="#c7ccd6" metalness={0.95} roughness={0.2} />
      </mesh>
      <mesh position={[0, 0, -0.02]}>
        <planeGeometry args={[1.8, 1.18]} />
        <meshStandardMaterial color="#0b0d12" metalness={0.6} roughness={0.4} />
      </mesh>
      {/* real sensor photo */}
      <mesh>
        <planeGeometry args={[1.72, 1.1]} />
        <meshStandardMaterial map={tex} roughness={0.5} metalness={0.1} />
      </mesh>
      {/* red accent bar */}
      <mesh position={[0, -0.68, 0]}>
        <planeGeometry args={[1.86, 0.045]} />
        <meshBasicMaterial color="#E63946" />
      </mesh>
    </group>
  );
};

const PulseRings = ({ position }) => {
  const refs = [useRef(), useRef(), useRef()];
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    refs.forEach((r, i) => {
      if (!r.current) return;
      const p = ((t / 2.2 + i / 3) % 1);
      const s = 0.4 + p * 1.6;
      r.current.scale.setScalar(s);
      r.current.material.opacity = 0.55 * (1 - p);
    });
  });
  return (
    <group position={position}>
      {refs.map((r, i) => (
        <mesh key={i} ref={r}>
          <torusGeometry args={[0.5, 0.012, 8, 48]} />
          <meshBasicMaterial color="#E63946" transparent opacity={0.4} />
        </mesh>
      ))}
    </group>
  );
};

const HeroGroup = ({ reduced }) => {
  const group = useRef();
  const tire = useRef();
  useFrame((state, delta) => {
    if (tire.current && !reduced) tire.current.rotation.z -= delta * 0.35;
    if (group.current && !reduced) {
      const { pointer } = state;
      group.current.rotation.y += (pointer.x * 0.35 - group.current.rotation.y) * 0.05;
      group.current.rotation.x += (-pointer.y * 0.18 - group.current.rotation.x) * 0.05;
    }
  });
  return (
    <group ref={group} position={[-0.4, 0, 0]}>
      <group ref={tire} position={[-0.7, -0.15, 0]}>
        <Tire />
      </group>
      <Float speed={reduced ? 0 : 2} rotationIntensity={0.25} floatIntensity={reduced ? 0 : 0.7}>
        <group position={[1.75, 0.55, 0.4]}>
          <RealSensorCard />
          <PulseRings position={[0, 0, -0.05]} />
          <Html position={[0, -0.95, 0]} center zIndexRange={[20, 0]}>
            <div className="font-mono text-[10px] tracking-widest text-red-400 bg-black/60 border border-red-500/40 rounded-full px-3 py-1 whitespace-nowrap backdrop-blur-sm">
              حساس حقيقي · TPMS 433MHz
            </div>
          </Html>
        </group>
      </Float>
      <Line
        points={[[0.35, 0.1, 0.2], [1.0, 0.4, 0.35], [1.5, 0.5, 0.4]]}
        color="#E63946"
        lineWidth={1.2}
        dashed
        dashSize={0.08}
        gapSize={0.05}
        transparent
        opacity={0.7}
      />
    </group>
  );
};

export const HeroScene = ({ reduced }) => (
  <Canvas
    camera={{ position: [0, 0.7, 5.4], fov: 42 }}
    dpr={[1, 1.75]}
    gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
    style={{ background: "transparent" }}
  >
    <ambientLight intensity={0.35} />
    <spotLight position={[4, 5, 4]} angle={0.5} penumbra={0.8} intensity={140} color="#ffffff" />
    <spotLight position={[-6, 3, -2]} angle={0.6} penumbra={1} intensity={60} color="#E63946" />
    <directionalLight position={[0, -2, 4]} intensity={0.5} color="#8ea2c0" />
    <HeroGroup reduced={reduced} />
    <ContactShadows position={[0, -1.9, 0]} opacity={0.55} scale={12} blur={2.6} far={4} color="#000000" />
  </Canvas>
);

export const HeroSceneSafe = ({ reduced, fallbackImage, fallbackAlt }) => {
  const [supported] = useState(isWebGLSupported);
  if (!supported) {
    return (
      <img
        src={fallbackImage}
        alt={fallbackAlt}
        className="w-full h-full object-cover rounded-2xl"
        loading="eager"
      />
    );
  }
  return (
    <CanvasBoundary
      fallback={
        <img
          src={fallbackImage}
          alt={fallbackAlt}
          className="w-full h-full object-cover rounded-2xl"
        />
      }
    >
      <HeroScene reduced={reduced} />
    </CanvasBoundary>
  );
};
